// Visual mesh of one character. 11 bone meshes (capsule/sphere) + inverted-hull outline.
// Reads snapshots from sim and lerps poses each frame.
// Squash-stretch on bones is purely visual — never touches colliders.

import * as THREE from 'three';
import { Bone, BONE_SPECS, BONE_COUNT } from '../sim/Ragdoll';
import type { CharacterSnapshot } from '../sim/SimState';
import { CONFIG } from '../config';
import { ToonMaterial } from './materials/ToonMaterial';
import { OutlineMaterial } from './materials/OutlineMaterial';
import { lerpBone } from './Interpolator';
import { loadHatModel, neutraliseHatMaterials, type HatId, type HatConfig } from './HatModels';

export interface CharacterPalette {
  base: string;
  shadow: string;
  mid: string;
  high: string;
  rim: string;
  outline: string;
}

const _vec = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _hatOffset = new THREE.Vector3();
const _hatEuler = new THREE.Euler();
const DEG2RAD = Math.PI / 180;

export interface CharacterViewOptions {
  /** Which hat to show, or null for no hat. */
  hatId?: HatId | null;
}

/** Input accepted by setHat: a registered id, null to clear, or a custom
 *  group + its own placement config (used for dev GLB uploads). */
export type HatInput = HatId | null | { group: THREE.Group; config: HatConfig };

export class CharacterView {
  readonly group = new THREE.Group();
  readonly material: ToonMaterial;
  readonly outlineMaterial: OutlineMaterial;
  private boneMeshes: THREE.Mesh[] = [];
  private outlineMeshes: THREE.Mesh[] = [];
  private squashKick: number[] = new Array(BONE_COUNT).fill(0);
  private squashAxis: THREE.Vector3[] = Array.from({ length: BONE_COUNT }, () => new THREE.Vector3(0, 1, 0));
  private damageFlash = 0;
  private hat: THREE.Group | null = null;
  private hatId: HatId | null;
  /** Live reference to the placement config for the currently-worn hat.
   *  Mutating this object updates the hat pose immediately on the next frame. */
  private hatConfig: HatConfig | null;
  /** Cached local-space hat quaternion derived from
   *  (pitchDeg, yawDeg, rollDeg). Recomputed only when those scalars
   *  change vs `_lastHat{Pitch,Yaw,Roll}`. The head pose stays per-frame
   *  (`hat.quaternion = head.quaternion * _hatLocalQuat`) — only the
   *  Euler→Quat conversion is dirty-flagged. NaN sentinels force the
   *  first refresh on a fresh hat. */
  private _hatLocalQuat = new THREE.Quaternion();
  private _lastHatPitch = NaN;
  private _lastHatYaw = NaN;
  private _lastHatRoll = NaN;

  constructor(palette: CharacterPalette, opts: CharacterViewOptions = {}) {
    this.hatId = opts.hatId ?? null;
    this.hatConfig = this.hatId ? CONFIG.hats[this.hatId] : null;
    this.material = new ToonMaterial({
      baseColor: palette.base,
      shadowColor: palette.shadow,
      midColor: palette.mid,
      highColor: palette.high,
      rimColor: palette.rim,
      shadowLift: 1.0
    });
    this.outlineMaterial = new OutlineMaterial(2.6, palette.outline);

    for (let i = 0; i < BONE_COUNT; i++) {
      const spec = BONE_SPECS[i]!;
      const geo = spec.type === 'sphere'
        ? new THREE.SphereGeometry(spec.radius * 1.02, 24, 18)
        : new THREE.CapsuleGeometry(spec.radius * 1.02, spec.halfHeight * 2, 6, 14);
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.frustumCulled = false;
      // Bones cast and receive shadow from the scene's directional light.
      // Receiving is gated by the toon material sampling shadow() in its
      // colorNode; the flag is set anyway for symmetry / future swaps.
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.boneMeshes.push(mesh);

      // Outline mesh — slightly larger via inverted-hull shader. Kept out of
      // the shadow pass: the back-side hull writes a shell of depth slightly
      // larger than the body, which would acne the body's own surface.
      const outlineGeo = spec.type === 'sphere'
        ? new THREE.SphereGeometry(spec.radius * 1.02, 18, 14)
        : new THREE.CapsuleGeometry(spec.radius * 1.02, spec.halfHeight * 2, 4, 10);
      const outlineMesh = new THREE.Mesh(outlineGeo, this.outlineMaterial);
      outlineMesh.frustumCulled = false;
      this.outlineMeshes.push(outlineMesh);

      // Parent the outline UNDER the body mesh: the outline used to live in
      // `this.group` next to the body and was hand-synced every frame
      // (position, quaternion, and a `scale.copy(m.scale)`). Making it a
      // child of the body mesh lets three's transform inheritance do that
      // work on the GPU side — we set the body's transform once and the
      // outline gets it for free. The outline keeps `scale=(1,1,1)` so its
      // effective world scale equals the body's, exactly matching the
      // pre-change behaviour. Render order isn't affected: both meshes
      // still draw in the default opaque pass with the same renderOrder.
      mesh.add(outlineMesh);
      this.group.add(mesh);
    }

    // Eyes — small black spheres parented to the head mesh so they inherit
    // its pose. Character local +Z is forward, so eyes sit on the +Z face.
    const headMesh = this.boneMeshes[Bone.Head]!;
    const headR = BONE_SPECS[Bone.Head]!.radius;
    const eyeGeo = new THREE.SphereGeometry(headR * 0.14, 12, 10);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0x0a0a12 });
    for (const s of [-1, +1]) {
      const eye = new THREE.Mesh(eyeGeo, eyeMat);
      eye.position.set(s * headR * 0.32, headR * 0.08, headR * 0.88);
      eye.frustumCulled = false;
      headMesh.add(eye);
    }

    // Hat — loaded async, parented at group level so head squash/stretch
    // doesn't distort it. Pose copied from the head mesh each frame.
    if (this.hatId) this.attachHat(this.hatId);
  }

  /** Load the given hat's GLB and parent it under this character. Guarded so a
   *  slow-loading hat that's been swapped out before it arrives just gets
   *  dropped instead of briefly appearing. */
  private attachHat(id: HatId): void {
    loadHatModel(id).then((template) => {
      if (this.hatId !== id) return; // user changed selection mid-load
      const h = template.clone(true);
      // `template` is already neutralised by `loadHatModel` (see HatModels.ts
      // — runs once per HatId before the scene lands in the cache).
      // Cloning shares materials by reference, so the neutralisation
      // propagates to this instance for free.
      enableShadowsOnMeshes(h);
      this.hat = h;
      this.group.add(h);
    }).catch((err) => {
      console.warn('[CharacterView] hat load failed:', err);
    });
  }

  /** Visual-only kick: stretches a bone along worldDir, springs back over time. */
  kickBone(boneIndex: number, worldDir: THREE.Vector3, strength: number): void {
    if (boneIndex < 0 || boneIndex >= BONE_COUNT) return;
    this.squashKick[boneIndex] = Math.max(this.squashKick[boneIndex]!, Math.min(0.45, strength));
    this.squashAxis[boneIndex]!.copy(worldDir).normalize();
  }

  pulseDamageFlash(): void {
    this.damageFlash = Math.min(1, this.damageFlash + 0.7);
  }

  update(prev: CharacterSnapshot | null, curr: CharacterSnapshot, alpha: number, dt: number): void {
    for (let i = 0; i < BONE_COUNT; i++) {
      lerpBone(prev, curr, i, alpha, _vec, _quat);
      const m = this.boneMeshes[i]!;
      m.position.copy(_vec); m.quaternion.copy(_quat);
      // Outline is now a child of the body mesh — three propagates `m`'s
      // transform to it for free. We never touch outline.position / .quaternion
      // / .scale here.

      // Squash-stretch
      const kick = this.squashKick[i]!;
      if (kick > 0.001) {
        // Stretch along squashAxis (in object space — fold via quaternion inverse)
        const axis = this.squashAxis[i]!;
        // Approximate: scale along world Y if bone is mostly upright; use simple uniform stretch
        const stretch = 1 + kick;
        const squish = 1 - kick * 0.45;
        m.scale.set(squish, stretch, squish);
        // Decay
        this.squashKick[i] = Math.max(0, kick - dt * 4.5);
      } else {
        m.scale.set(1, 1, 1);
      }
    }
    // Damage flash decay
    this.damageFlash = Math.max(0, this.damageFlash - dt * 4.5);
    this.material.setDamageFlash(this.damageFlash);

    // Hat — copy head pose, apply local-Y offset so the hat rides the top of
    // the head regardless of tilt. Kept at group level (not parented to the
    // head mesh) so squash-stretch on impact doesn't deform it. Each hat has
    // its own placement config (CONFIG.hats[id]) stored in this.hatConfig.
    this.updateHatPose();
  }

  /** Per-rAF mesh update from a pre-baked render-format bone buffer (7
   *  floats/bone: pos+quat). Used by the renderer for remote characters
   *  whose bones come from `ColyseusTransport.remoteBonesAt(charId)` —
   *  bracket-interpolated at the actual rAF wall instant, so no further
   *  prev/curr alpha-lerp is needed (or wanted: lerping between two samples
   *  of the same bracket curve buys nothing). Squash-stretch decay, damage
   *  flash, and hat tracking match `update()` exactly. */
  updateLiveBones(bones: Float32Array, dt: number): void {
    for (let i = 0; i < BONE_COUNT; i++) {
      const b = i * 7;
      _vec.set(bones[b]!, bones[b + 1]!, bones[b + 2]!);
      _quat.set(bones[b + 3]!, bones[b + 4]!, bones[b + 5]!, bones[b + 6]!);
      const m = this.boneMeshes[i]!;
      m.position.copy(_vec); m.quaternion.copy(_quat);
      // Outline is parented under the body mesh — its transform is inherited.

      const kick = this.squashKick[i]!;
      if (kick > 0.001) {
        const stretch = 1 + kick;
        const squish = 1 - kick * 0.45;
        m.scale.set(squish, stretch, squish);
        this.squashKick[i] = Math.max(0, kick - dt * 4.5);
      } else {
        m.scale.set(1, 1, 1);
      }
    }
    this.damageFlash = Math.max(0, this.damageFlash - dt * 4.5);
    this.material.setDamageFlash(this.damageFlash);

    this.updateHatPose();
  }

  /** Per-frame hat pose. Position/quaternion track the head's live pose;
   *  the local Euler→Quat (pitch/yaw/roll from the placement config) is
   *  dirty-flagged via `_lastHat{Pitch,Yaw,Roll}` so it only rebuilds
   *  when the debug-panel sliders mutate the config. Steady-state: zero
   *  Euler conversions per frame. */
  private updateHatPose(): void {
    if (!this.hat || !this.hatConfig) return;
    const hatCfg = this.hatConfig;
    const headMesh = this.boneMeshes[Bone.Head]!;
    if (
      hatCfg.pitchDeg !== this._lastHatPitch ||
      hatCfg.yawDeg   !== this._lastHatYaw ||
      hatCfg.rollDeg  !== this._lastHatRoll
    ) {
      _hatEuler.set(
        hatCfg.pitchDeg * DEG2RAD,
        hatCfg.yawDeg * DEG2RAD,
        hatCfg.rollDeg * DEG2RAD,
        'YXZ'
      );
      this._hatLocalQuat.setFromEuler(_hatEuler);
      this._lastHatPitch = hatCfg.pitchDeg;
      this._lastHatYaw = hatCfg.yawDeg;
      this._lastHatRoll = hatCfg.rollDeg;
    }
    _hatOffset.set(hatCfg.offsetX, hatCfg.offsetY, hatCfg.offsetZ).applyQuaternion(headMesh.quaternion);
    this.hat.position.copy(headMesh.position).add(_hatOffset);
    this.hat.quaternion.copy(headMesh.quaternion).multiply(this._hatLocalQuat);
    this.hat.scale.setScalar(hatCfg.scale);
  }

  /** Swap which hat this character wears. Accepts:
   *   - a registered HatId ('top', 'chef', …) → loads from the model registry
   *     and uses CONFIG.hats[id] as its placement config.
   *   - `{ group, config }` → custom mesh + placement config (dev uploads).
   *   - null → no hat.
   *
   *  Dedup is done on hatId alone — NOT on `this.hat` — because attachHat is
   *  async. If we early-return only when `this.hat` is attached, a same-id
   *  call made while the first load is still in flight kicks off a SECOND
   *  attachHat; both .then callbacks clone + add to the group, and only one
   *  ends up tracked in `this.hat`. The orphan sits at group origin forever
   *  because there's no frame-update driving its position. */
  setHat(input: HatInput): void {
    if (typeof input === 'string' && this.hatId === input) return;
    if (this.hat) {
      this.group.remove(this.hat);
      this.hat = null;
    }
    if (input === null) {
      this.hatId = null;
      this.hatConfig = null;
    } else if (typeof input === 'string') {
      this.hatId = input;
      this.hatConfig = CONFIG.hats[input];
      this.attachHat(input);
    } else {
      // Custom scene (uploaded GLB etc.). Clone so we don't mutate the
      // caller's object; neutralise metalness so it renders without an envMap.
      this.hatId = null;
      this.hatConfig = input.config;
      const h = input.group.clone(true);
      neutraliseHatMaterials(h);
      enableShadowsOnMeshes(h);
      this.hat = h;
      this.group.add(h);
    }
  }

  setPalette(palette: CharacterPalette): void {
    this.material.setBaseColor(palette.base);
    this.material.setShadowColor(palette.shadow);
    this.material.setMidColor(palette.mid);
    this.material.setHighColor(palette.high);
    this.material.setRimColor(palette.rim);
    this.outlineMaterial.setColor(palette.outline);
  }

  static palettePlayer(): CharacterPalette {
    return {
      base: '#ffd56b', shadow: '#5a3b6e', mid: '#e3a48e', high: '#fff2c2',
      rim: '#ffe6a8', outline: '#0d0816'
    };
  }
  static paletteAI(): CharacterPalette {
    return {
      base: '#7ec4ff', shadow: '#2a3868', mid: '#7290c9', high: '#e2f0ff',
      rim: '#a8e0ff', outline: '#0a0c1c'
    };
  }
}

function enableShadowsOnMeshes(root: THREE.Object3D): void {
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
}
